import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import Layout from "../../components/Layout/Layout";
import { useEffect, useState } from "react";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Routes from "../../helpers/Routes";
import { SupplierDTO } from "../../models/Supplier/SupplierDTO";
import { SupplierService } from "../../services/SupplierService";
import { Parameter } from "../../models/Parameter";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";

export default function SupplierIndex() {
  //Declaração de estados e variáveis importantes
  const navigate = useNavigate();
  const intl = useIntl();
  const supplierService = new SupplierService();
  const pageSizes: number[] = [5, 15, 30];
  const [data, setData] = useState<SupplierDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [searchParams] = useSearchParams();

  //Vamos tentar obter a page e a pageSize pelos query parameters
  const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
  const [pageSize] = useState<number>(
    Utilities.LoadPageSizeFromURLQuery(pageSizes)
  );

  //Search Parameters
  const [supplierName, setSupplierName] = useState<string>(
    Utilities.LoadParameterFromURLQuery("Name", "string", "")
  );

  const [currentPage, setCurrentPage] = useState<number>();
  const [currentPageSize, setCurrentPageSize] = useState<number>();
  const [currentSorting, setCurrentSorting] = useState<
    SortingRule<object>[] | null
  >(null);
  const [goToPage, setGoToPage] = useState<number>();
  const [initialSortBy] = useState<SortingRule<object>[]>(
    Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder")
  );
  const [reloadSearch, setReloadSearch] = useState<boolean>(false);

  const { availableModules } = useAvailableModulesContext();
  const [isActiveErpSuppliers, setIsActiveErpSuppliers] = useState<boolean>(false);

  useEffect(() => {
    if (availableModules.includes("IsActiveErpSuppliers")) {
      setIsActiveErpSuppliers(true);
    }
  }, [availableModules]);

  useEffect(() => {
    fetchData(page || 1, pageSize || 5, initialSortBy);
  }, []);

  useEffect(() => {
    if (reloadSearch === true) {
      fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
      setReloadSearch(false);
    }
    return () => { };
  }, [reloadSearch]);

  const columns = React.useMemo(
    () => [
      {
        Header: "",
        accessor: " ",
        Cell: (props: any) =>
          OperationsCell(props.cell.row.original.id, "supplier"),
        width: 5,
      },
      {
        Header: <FormattedMessage id="Screens.Suppliers.Table.Name" />,
        accessor: "name",
        width: 20,
      },
      {
        Header: <FormattedMessage id="Screens.Suppliers.Table.Email" />,
        accessor: "emailAddress",
        width: 20,
      },
      {
        Header: <FormattedMessage id="Screens.Suppliers.Table.Phone" />,
        accessor: "phoneNumber",
        width: 15,
      },
      {
        Header: <FormattedMessage id="Screens.Suppliers.Table.Type" />,
        accessor: "type",
        width: 15,
      },
      {
        Header: <FormattedMessage id="Screens.Suppliers.Table.Status" />,
        accessor: "active",
        width: 10,
        Cell: (props: any) => {
          return props.cell.value ? (
            <span>{intl.formatMessage({ id: "Screens.Suppliers.Status.Active" })}</span>
          ) : (
            <span>{intl.formatMessage({ id: "Screens.Suppliers.Status.Inactive" })}</span>
          );
        },
      },
    ],
    []
  );

  const fetchData = async (
    page: number,
    pageSize: number,
    sortBy: SortingRule<object>[] | null
  ) => {
    //Atualizamos na history do browser a current page e pageSize
    var searchUrl: string = `?page=${page}&pageSize=${pageSize}` +
      `${Utilities.AddParamToQueryIfValue("Name", "string", supplierName)}`;

    if (sortBy != null && sortBy.length > 0) {
      searchUrl += `${Utilities.AddParamToQueryIfValue(
        "sortField",
        "string",
        sortBy[0].id
      )}${Utilities.AddParamToQueryIfValue(
        "sortOrder",
        "string",
        sortBy[0].desc === true ? "DESC" : "ASC"
      )}`;
    }

    //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota
    if (searchParams.toString() === "") {
      navigate(`/suppliers${searchUrl}`, { replace: true });
    } else {
      //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
      navigate(`/suppliers${searchUrl}`);
    }

    setLoading(true);

    //Vamos obter os dados
    const searchParameters: Parameter[] = mountSearchParameters();
    const sortingParameters: Parameter[] = Utilities.MountSortParameters(sortBy);

    var response = await supplierService.GetAll(
      searchParameters,
      sortingParameters,
      page,
      pageSize
    );

    if (response.success !== true) {
      setData([]);
      setPageCount(0);
      setLoading(false);
      setCurrentPageSize(0);
      setCurrentPage(0);
      setCurrentSorting(null);
      Toast.Show("error", response.message);
      return;
    }

    //Atribuímos os dados
    setData(response.items);
    setPageCount(Math.ceil(response.totalRecords / pageSize));
    setLoading(false);
    setCurrentPageSize(pageSize);
    setCurrentPage(page);
    setCurrentSorting(sortBy);
    setGoToPage(0);

    //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
    if (response.totalPages > 0 && page > response.totalPages) {
      setGoToPage(1);
    }
  };

  //Método para montar os parâmetros de pesquisa
  const mountSearchParameters = (): Parameter[] => {
    var parameters: Parameter[] = [];
    parameters.push(new Parameter("Name", supplierName));
    return parameters;
  };

  const onEnter = () => {
    fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
  };

  const onButtonClick = async () => {
    setCurrentPage(1);
    setGoToPage(1);
    fetchData(1, currentPageSize ?? 5, currentSorting);
  };

  const onRemoveFiltersClick = async () => {
    setCurrentPage(1);
    setGoToPage(1);
    setSupplierName("");
    setReloadSearch(true);
  };

  const onNewSupplierClick = () => {
    navigate(Routes.suppliers.create);
  };

  return (
    <Layout>
      <div className="pageContainer">
        <div className="pageHeader">
          <span className="pageTitle">
            <FormattedMessage id="Screens.Suppliers.Title" />
          </span>

          <Row>
            <Col
              xl={2}
              lg={3}
              md={3}
              sm={12}
              xs={12}
              style={{ marginTop: "1em", marginBottom: "1em" }}
            >
              <ButtonComponent
                text={intl.formatMessage({ id: "Screens.Suppliers.Button.CreateSupplier" })}
                onClick={onNewSupplierClick}
                disabled={isActiveErpSuppliers}
              />
            </Col>
          </Row>
        </div>

        <div className="pageBody">
          <Row>
            <InputComponent
              xl={2}
              lg={3}
              md={5}
              sm={12}
              xs={12}
              style={{ width: "100%" }}
              type={"text"}
              onEnter={onEnter}
              value={supplierName}
              label={intl.formatMessage({ id: "Screens.Suppliers.Table.Name" })}
              readOnly={false}
              onChange={(t: string) => setSupplierName(t)}
            />
          </Row>

          <Row>
            <Col
              xl={6}
              lg={6}
              md={6}
              sm={12}
              xs={12}
              style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
            >
              <ButtonComponent
                text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                onClick={onButtonClick}
                style={{ minWidth: "100px" }}
              />
              <ButtonComponent
                text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                onClick={onRemoveFiltersClick}
                style={{ minWidth: "113px" }}
              />
            </Col>
          </Row>

          <div className="pageComponent" style={{ width: "100%" }}>
            <TableComponent
              goToPage={goToPage}
              loading={loading}
              pageCount={pageCount}
              columns={columns}
              manualPagination={true}
              fetchData={fetchData}
              pageSizes={pageSizes}
              initialPage={page}
              initialPageSize={pageSize}
              initialSortBy={initialSortBy}
              data={data}
              dontOrderFirstCollumn={true}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}
